import { z } from 'zod';

// Enhanced validation schema using Zod
export const registrationSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(2, 'Անունը պետք է պարունակի նվազագույնը 2 նշան')
    .max(50, 'Անունը չպետք է գերազանցի 50 նշանը')
    .regex(/^[ա-֊\u0531-\u0587a-zA-Z\s]+$/, 'Անունը կարող է պարունակել միայն տառեր'),

  lastName: z
    .string()
    .trim()
    .min(2, 'Ազգանունը պետք է պարունակի նվազագույնը 2 նշան')
    .max(50, 'Ազգանունը չպետք է գերազանցի 50 նշանը')
    .regex(/^[ա-֊\u0531-\u0587a-zA-Z\s]+$/, 'Ազգանունը կարող է պարունակել միայն տառեր'),

  email: z
    .string()
    .trim()
    .email('Էլ․ փոստի հասցեն սխալ ձևաչափի է')
    .max(100, 'Էլ․ փոստի հասցեն չպետք է գերազանցի 100 նշանը')
    .toLowerCase()
    .refine(
      (email) => !email.includes(' '), 
      'Էլ․ փոստի հասցեն չպետք է պարունակի բացատներ'
    ),

  phone: z
    .string()
    .trim()
    .regex(
      /^\+374[0-9]{8}$/, 
      'Հեռախոսահամարը պետք է լինի հայկական ձևաչափով (+374XXXXXXXX)'
    ),

  course: z
    .string()
    .min(1, 'Ընտրեք դասընթացը'),

  faculty: z
    .string()
    .optional(),

  specialization: z
    .string()
    .optional(),

  language: z
    .string()
    .optional(),

  learningType: z
    .string()
    .min(1, 'Ընտրեք ուսուցման տեսակը')
    .refine(val => ['group', 'individual'].includes(val), {
      message: 'Ընտրեք ուսուցման տեսակը'
    }),

  learningFormat: z
    .string()
    .min(1, 'Ընտրեք ուսուցման ձևը')
    .refine(val => ['online', 'inPerson'].includes(val), {
      message: 'Ընտրեք ուսուցման ձևը'
    }),

  duration: z
    .string()
    .min(1, 'Ընտրեք դասընթացի տևողությունը')
});

export type RegistrationFormData = z.infer<typeof registrationSchema>;

// Custom validation for course-specific requirements
export const validateCourseSpecificFields = (data: RegistrationFormData) => {
  const errors: string[] = [];

  // Web programming requires specialization
  if (data.course === 'web-programming' && !data.specialization) {
    errors.push('Վեբ ծրագրավորման համար ընտրեք մասնագիտացումը');
  }

  // Backend specialization or programming languages require language
  if (
    (data.course === 'web-programming' && data.specialization === 'backend') ||
    data.course === 'programming-languages'
  ) {
    if (!data.language) {
      errors.push('Ընտրեք ծրագրավորման լեզուն');
    }
  }

  return errors;
};

// Sanitization utilities
export const sanitizeInput = (input: string): string => {
  return input
    .trim()
    .replace(/[<>\"'&]/g, '') // Remove potentially harmful characters
    .slice(0, 1000); // Limit length
};

export const sanitizeFormData = (data: RegistrationFormData): RegistrationFormData => {
  return {
    ...data,
    firstName: sanitizeInput(data.firstName),
    lastName: sanitizeInput(data.lastName),
    email: sanitizeInput(data.email).toLowerCase(),
    phone: sanitizeInput(data.phone),
    course: sanitizeInput(data.course),
    faculty: data.faculty ? sanitizeInput(data.faculty) : '',
    specialization: data.specialization ? sanitizeInput(data.specialization) : '',
    language: data.language ? sanitizeInput(data.language) : '',
  };
};